// A concrete {@link BlockPut} that stores a site block on IPFS via a Kubo RPC node's
// `/api/v0/block/put` endpoint (szzt-spec.md §13.1, roadmap item 7). It is the IPFS analog of
// `r2BucketPut`: the publisher holds the node's API credentials and pushes each block itself, no
// server in the middle, and the gateway still re-hashes every byte on read (the mirror is untrusted).
//
// CID-EXACTNESS. `block/put?cid-codec=<raw|dag-cbor>&mhtype=sha2-256` stores the bytes as ONE block
// and returns the CIDv1 IPFS computed for them. We compare that returned CID to the block's CID and
// REJECT on any mismatch, so a resolved push proves the block is retrievable under exactly our CID —
// the same guarantee R2's signed content-hash gives, enforced by Kubo's own hashing. This is what
// dodges the codec trap (see ipfs-producer.ts): we never let IPFS wrap the file in UnixFS `dag-pb` and
// re-chunk it under a different root — one raw/dag-cbor block in, the same CID back.
//
// BIG BLOCKS. Our per-file ceiling is 5 MiB (§3.1) and each file is a single `raw` block, so blocks
// can exceed Kubo's 1 MiB soft `block/put` limit — hence `allow-big-block=true`. Caveat inherent to
// CID-exact single-block IPFS: blocks over ~2 MiB cannot move over bitswap between nodes, so such a
// block is only retrievable from a gateway co-located with the node that pinned it (which serves its
// own local block without bitswap). The `ipfs:<gatewayHost>` hint points at exactly that gateway.
//
// Runs unchanged in a browser or modern Node (both provide `fetch`). The multipart body is assembled
// as raw bytes (no `FormData`/`Blob`) to stay within this package's DOM-free `lib` (ES2022 only).
import type { MirrorBlock } from "./types.ts";
import type { BlockPut } from "./ipfs-producer.ts";

/** Connection details for the Kubo RPC node the publisher pushes blocks into. */
export interface KuboConfig {
  /**
   * Base URL of the Kubo RPC API, e.g. `https://ipfs.example.com:5001` (the node exposing
   * `/api/v0/block/put`). No trailing slash required. This is the WRITE endpoint, distinct from the
   * public gateway host advertised in the `ipfs:` hint.
   */
  apiUrl: string;
  /**
   * Optional `Authorization` header value for the RPC API (e.g. `Basic <base64>` or `Bearer <token>`).
   * A bare, unauthenticated Kubo RPC must never be public, so most real deployments set this. Never
   * logged; lives only in the caller's config.
   */
  authorization?: string;
}

/** Injectable seam so the pusher is unit-testable without a live Kubo node. Defaults to global `fetch`. */
export interface KuboBlockPutDeps {
  fetch?: typeof fetch;
}

/** Thrown when the Kubo RPC rejects the block upload (auth, node error, big-block refusal, quota…). */
export class KuboBlockPutError extends Error {
  constructor(
    readonly cid: string,
    readonly status: number,
    readonly body: string,
  ) {
    super(`Kubo block/put ${cid} failed: HTTP ${status}${body ? ` — ${body.slice(0, 300)}` : ""}`);
    this.name = "KuboBlockPutError";
  }
}

/**
 * Thrown when Kubo stored the block but under a CID other than the one we intended — the store would
 * not resolve under our hint (§13.1). Fail-safe: aborts the push so NO `ipfs:` hint is written rather
 * than advertising a mirror that lacks the exact block.
 */
export class KuboCidMismatchError extends Error {
  constructor(
    readonly expected: string,
    readonly actual: string,
  ) {
    super(`Kubo stored block under ${actual}, expected ${expected} (§13.1) — refusing to write a dead hint`);
    this.name = "KuboCidMismatchError";
  }
}

const CODEC_PARAM: Record<MirrorBlock["codec"], string> = {
  raw: "raw",
  "dag-cbor": "dag-cbor",
};

// A fixed multipart boundary. Kubo matches file parts by their content-disposition, not by uniqueness,
// and there is no secret to hide, so a constant boundary keeps the pusher deterministic (no RNG, which
// this package's runtime bans anyway). It is long/distinctive enough not to collide with block bytes.
const BOUNDARY = "----SzztKuboBlockBoundary8f2c1a";

const utf8 = new TextEncoder();

/** Wrap raw block bytes in a single-file `multipart/form-data` body Kubo's `block/put` accepts. */
function multipartBody(bytes: Uint8Array): Uint8Array {
  const head = utf8.encode(
    `--${BOUNDARY}\r\n` +
      `Content-Disposition: form-data; name="file"; filename="block"\r\n` +
      `Content-Type: application/octet-stream\r\n\r\n`,
  );
  const tail = utf8.encode(`\r\n--${BOUNDARY}--\r\n`);
  const out = new Uint8Array(head.length + bytes.length + tail.length);
  out.set(head, 0);
  out.set(bytes, head.length);
  out.set(tail, head.length + bytes.length);
  return out;
}

/**
 * Build a {@link BlockPut} that stores each block on the Kubo node at `config.apiUrl` and verifies the
 * returned CID equals the block's CID. Pair with `ipfsBlockProducer({ gatewayHost, put })` where
 * `gatewayHost` is the node's PUBLIC gateway (which serves `https://<cid>.ipfs.<host>/`). Rejects with
 * {@link KuboBlockPutError} on any non-2xx and {@link KuboCidMismatchError} on a CID mismatch — either
 * fails the publish's all-or-nothing push, so no hint is written.
 *
 * Requires Kubo ≥ 0.18 (the `cid-codec` parameter; older nodes used `format`). The RPC API must permit
 * cross-origin `POST` from the publisher's origin when run in a browser (Access-Control-Allow-Origin).
 */
export function kuboBlockPut(config: KuboConfig, deps: KuboBlockPutDeps = {}): BlockPut {
  const doFetch = deps.fetch ?? fetch;
  const base = config.apiUrl.replace(/\/+$/, "");

  return async (block: MirrorBlock): Promise<void> => {
    // One block in → the same block out: single-block store under our exact CIDv1/sha-256/<codec>.
    const params = new URLSearchParams({
      "cid-codec": CODEC_PARAM[block.codec],
      mhtype: "sha2-256",
      pin: "true", // direct-pin so GC never drops the block out from under the hint
      "allow-big-block": "true", // our single-block raw files can exceed Kubo's 1 MiB soft limit (§3.1)
    });
    const url = `${base}/api/v0/block/put?${params.toString()}`;

    const headers: Record<string, string> = {
      "content-type": `multipart/form-data; boundary=${BOUNDARY}`,
    };
    if (config.authorization !== undefined) headers["authorization"] = config.authorization;

    const res = await doFetch(url, { method: "POST", headers, body: multipartBody(block.bytes) });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new KuboBlockPutError(block.cid, res.status, body);
    }

    // block/put returns one JSON object `{"Key":"<cidv1>","Size":<n>}`. Kubo emits base32 CIDv1 for
    // non-dag-pb codecs by default, matching our @atcute/cid base32 strings; a differing multibase (a
    // node configured otherwise) surfaces here as a mismatch and fails safe rather than a dead hint.
    const text = await res.text();
    let key: unknown;
    try {
      key = (JSON.parse(text) as { Key?: unknown }).Key;
    } catch {
      throw new KuboBlockPutError(block.cid, res.status, `unparseable block/put response: ${text}`);
    }
    if (typeof key !== "string" || key === "") {
      throw new KuboBlockPutError(block.cid, res.status, `block/put response missing Key: ${text}`);
    }
    if (key !== block.cid) throw new KuboCidMismatchError(block.cid, key);
  };
}
